import { COLS, PALETTE, ROWS } from "#puf/constants.ts";
import type { StoreContext } from "#puf/store/context.ts";
import { collectStateIds } from "@pixelpuf/schemas/pxpf";

const DESIGN_SESSION_STORAGE_KEY = "pixelpuf:design-session";

interface ThumbWindowEntry {
  frameIndex: number;
  thumb: string;
}

interface EditorSummary {
  projectName: string;
  tab: "canvas" | "state";
  cols: number;
  frameCount: number;
  activeFrame: number;
  activeLayerId: string;
  layerCount: number;
  layerIds: string[];
  visibleLayerIds: string[];
  color: string;
  brushSize: number;
  mode: StoreContext["mode"];
  onion: boolean;
  onionOpacity: number;
  fps: number;
  machineInitial: string | null;
  rows: number;
  stateIds: string[];
  stateRanges: Record<string, { start: number; end: number }>;
  transitionCount: number;
  selectedStateId: string | null;
  selectedTransitionIndex: number | null;
  /**
   * Project's effective drawing palette. Index `i` here corresponds to RLE
   * palette index `i + 1` (RLE index 0 is reserved for transparent). Lets
   * the agent emit `commit_frame` / `commit_tilemap` against the editor's
   * current palette without guessing or shipping a `paletteOverride`.
   * Currently sourced from the static `PALETTE` constant (no per-project
   * dynamic palette exists yet).
   */
  palette: string[];
  /**
   * PNG data URLs for the active frame and up to 2 neighbors. Capped to
   * keep the agent context small. The agent can pass any of these to
   * `render_and_critique`. Empty / never-rendered thumbs are dropped.
   */
  thumbs: ThumbWindowEntry[];
}

export function getOrCreateDesignSessionName(): string {
  try {
    const existing = window.localStorage.getItem(DESIGN_SESSION_STORAGE_KEY);
    if (existing) return existing;
    const created = `studio-${crypto.randomUUID()}`;
    window.localStorage.setItem(DESIGN_SESSION_STORAGE_KEY, created);
    return created;
  } catch {
    return `studio-${crypto.randomUUID()}`;
  }
}

export function summarizeEditorContext(ctx: StoreContext): EditorSummary {
  const activeLayers = ctx.frames[ctx.activeFrame] ?? [];
  const stateRanges = Object.fromEntries(
    Object.entries(ctx.stateFrames).map(([id, [start, end]]) => [id, { start, end }]),
  );
  return {
    projectName: ctx.projectName,
    tab: ctx.tab,
    cols: COLS,
    frameCount: ctx.frames.length,
    activeFrame: ctx.activeFrame,
    activeLayerId: ctx.activeLayerId,
    layerCount: activeLayers.length,
    layerIds: activeLayers.map((layer) => layer.id),
    visibleLayerIds: activeLayers.filter((layer) => layer.visible).map((layer) => layer.id),
    color: ctx.color,
    brushSize: ctx.brushSize,
    mode: ctx.mode,
    onion: ctx.onion,
    onionOpacity: ctx.onionOpacity,
    fps: ctx.fps,
    machineInitial: ctx.stateMachine?.initial ?? null,
    rows: ROWS,
    stateIds: ctx.stateMachine ? [...collectStateIds(ctx.stateMachine)] : [],
    stateRanges,
    transitionCount: ctx.stateMachine?.transitions.length ?? 0,
    selectedStateId: ctx.selectedStateId,
    selectedTransitionIndex: ctx.selectedTransitionIndex,
    palette: [...PALETTE],
    thumbs: collectThumbWindow(ctx.thumbs, ctx.activeFrame),
  };
}

/**
 * Pick the active frame's thumb plus up to one neighbor on each side.
 * Skips empty strings (frames that have not been rendered yet). Bounds the
 * agent's context window: 32x32 PNG thumbs are tiny but the data URL still
 * costs tokens, so cap to <=3 entries.
 */
function collectThumbWindow(thumbs: readonly string[], activeFrame: number): ThumbWindowEntry[] {
  const window: ThumbWindowEntry[] = [];
  const order = [activeFrame, activeFrame - 1, activeFrame + 1];
  for (const frameIndex of order) {
    if (frameIndex < 0 || frameIndex >= thumbs.length) continue;
    const thumb = thumbs[frameIndex];
    if (!thumb) continue;
    window.push({ frameIndex, thumb });
  }
  return window;
}
